import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInstantMotion } from '../../hooks/useInstantMotion';
import { wwwRouteEnter } from '../../lib/wwwRouteMotion';
import { WWW_TAP_TARGET_CLASS } from '../../lib/wwwTapTarget';
import { LandingSectionHeading } from './LandingSectionHeading';
import {
  LANDING_PRODUCT_SHOTS,
  LANDING_PRODUCT_SHOTS_HEADING,
  LANDING_PRODUCT_SHOTS_HEADING_ACCENT,
  LANDING_PRODUCT_SHOTS_HEADING_LEAD,
  LANDING_PRODUCT_SHOTS_SECTION_ID,
  LANDING_PRODUCT_SHOT_AUTOPLAY_MS,
  cycleLandingShotIndex,
  landingShotCoverRole,
} from './landingProductShotData';
import './LandingProductShots.css';

const SHOT_COUNT = LANDING_PRODUCT_SHOTS.length;

const CONTROL_BUTTON_CLASS = `${WWW_TAP_TARGET_CLASS} landing-coverflow__nav inline-flex items-center justify-center rounded-full bg-[var(--bg-card)] text-stone-700 dark:text-stone-200 shadow-[var(--shadow-panel)]`;

const DOT_BUTTON_CLASS = `${WWW_TAP_TARGET_CLASS} inline-flex items-center justify-center`;

const DOT_MARK_CLASS = 'h-2.5 w-2.5 rounded-full';

const OS_REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function useOsPrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(OS_REDUCED_MOTION_QUERY).matches;
  });

  useEffect(() => {
    const media = window.matchMedia(OS_REDUCED_MOTION_QUERY);
    const sync = () => setReduced(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  return reduced;
}

export function LandingProductShots() {
  const skipMotion = useInstantMotion();
  const prefersReducedMotion = useOsPrefersReducedMotion();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  const goBy = useCallback((delta) => {
    setCurrent((index) => cycleLandingShotIndex(index, delta, SHOT_COUNT));
  }, []);

  const goTo = useCallback((nextIndex) => {
    setCurrent(nextIndex);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion || paused || SHOT_COUNT < 2) {
      return undefined;
    }
    const timerId = window.setInterval(() => {
      setCurrent((index) => cycleLandingShotIndex(index, 1, SHOT_COUNT));
    }, LANDING_PRODUCT_SHOT_AUTOPLAY_MS);
    return () => window.clearInterval(timerId);
  }, [prefersReducedMotion, paused]);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goBy(1);
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goBy(-1);
    }
  };

  const currentShot = LANDING_PRODUCT_SHOTS[current];

  return (
    <section
      id={LANDING_PRODUCT_SHOTS_SECTION_ID}
      data-section={LANDING_PRODUCT_SHOTS_SECTION_ID}
      data-testid="landing-product-shots"
      className="py-8 sm:py-12 bg-[var(--surface-raised)] overflow-x-clip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={wwwRouteEnter(skipMotion, 16)}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative z-20 text-center mb-8 sm:mb-12"
        >
          <LandingSectionHeading
            id="landing-product-shots-heading"
            lead={`${LANDING_PRODUCT_SHOTS_HEADING_LEAD},`}
            accent={LANDING_PRODUCT_SHOTS_HEADING_ACCENT}
            ariaLabel={LANDING_PRODUCT_SHOTS_HEADING}
          />
        </motion.div>

        <div
          role="region"
          aria-roledescription="carousel"
          aria-labelledby="landing-product-shots-heading"
          data-testid="landing-product-shot-carousel"
          className="relative"
          onKeyDown={onKeyDown}
          onMouseEnter={() => {
            if (!skipMotion) setPaused(true);
          }}
          onMouseLeave={() => {
            if (!skipMotion) setPaused(false);
          }}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setPaused(false);
            }
          }}
        >
          <div className="relative">
            <div
              className="landing-coverflow min-w-0"
              data-reduced={prefersReducedMotion ? 'true' : undefined}
              data-testid="landing-product-shot-track"
              data-offset={current}
            >
              <div className="landing-coverflow__stage">
                {LANDING_PRODUCT_SHOTS.map((shot, shotIndex) => {
                  const role = landingShotCoverRole(shotIndex, current, SHOT_COUNT);
                  const isCurrent = role === 'current';
                  return (
                    <figure
                      key={shot.id}
                      className="landing-coverflow__slide m-0"
                      data-product-shot={shot.id}
                      data-current={isCurrent ? '' : undefined}
                      data-next={role === 'next' ? '' : undefined}
                      data-previous={role === 'previous' ? '' : undefined}
                      data-hidden={role === 'hidden' ? '' : undefined}
                    >
                      <img
                        src={shot.src}
                        alt={isCurrent ? shot.alt : ''}
                        aria-hidden={isCurrent ? undefined : true}
                        width={shot.width}
                        height={shot.height}
                        className="landing-coverflow__image"
                        loading={shotIndex === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                    </figure>
                  );
                })}
              </div>
            </div>

            <div className="landing-coverflow__controls">
              <button
                type="button"
                className={`${CONTROL_BUTTON_CLASS} landing-coverflow__nav--prev`}
                aria-label="Previous screenshots"
                data-testid="landing-product-shot-prev"
                onClick={() => goBy(-1)}
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${CONTROL_BUTTON_CLASS} landing-coverflow__nav--next`}
                aria-label="Next screenshots"
                data-testid="landing-product-shot-next"
                onClick={() => goBy(1)}
              >
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="relative z-20 mt-5 text-center" aria-live="polite">
            <p className="instrument-page-eyebrow !mb-1">{currentShot.kicker}</p>
            <p
              className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-stone-900 dark:text-white"
              data-testid="landing-product-shot-caption"
            >
              {currentShot.caption}
            </p>
          </div>

          <div className="mt-4 flex justify-center gap-1">
            {LANDING_PRODUCT_SHOTS.map((item, shotIndex) => {
              const isCurrent = shotIndex === current;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={DOT_BUTTON_CLASS}
                  aria-label={`Show ${item.caption}`}
                  aria-current={isCurrent ? 'true' : undefined}
                  data-testid={`landing-product-shot-dot-${item.id}`}
                  onClick={() => goTo(shotIndex)}
                >
                  <span
                    className={`${DOT_MARK_CLASS} ${
                      isCurrent
                        ? 'bg-primary-500'
                        : 'bg-stone-300 dark:bg-stone-600'
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
